import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const SpinnerPlay = ({ questions, handleAnswer, onComplete, setCurrentSpinnerQuestion }) => {
    const [spinning, setSpinning] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [answered, setAnswered] = useState(false);
    const [clickedIndex, setClickedIndex] = useState(null);
    const [started, setStarted] = useState(false);
    const [remainingIndexes, setRemainingIndexes] = useState([]);
    const spinnerRef = useRef(null);
    const previousRotation = useRef(0);
    const { t } = useTranslation();

    const handleSpin = () => {
        if (spinning || remainingIndexes.length === 0) {
            return;
        }

        const itemCount = remainingIndexes.length;
        const randomIdx = Math.floor(Math.random() * itemCount);
        const selected = remainingIndexes[randomIdx];

        setRemainingIndexes(prev => prev.filter((_, i) => i !== randomIdx));

        const degreesPerOption = 360 / questions.length;
        const currentRotation = previousRotation.current % 360;
        const targetAngle = 360 - selected * degreesPerOption;

        let correction = targetAngle - currentRotation;
        if (correction < 0) correction += 360;

        const fullRotations = 360 * 3;
        const rotation = previousRotation.current + fullRotations + correction;

        setSpinning(true);
        setSelectedIndex(null);
        setAnswered(false);
        setClickedIndex(null);
        previousRotation.current = rotation;

        if (spinnerRef.current) {
            spinnerRef.current.style.transition = 'transform 3s ease-out';
            spinnerRef.current.style.transform = `rotate(${rotation}deg)`;
        }

        setTimeout(() => {
            setSpinning(false);
            setSelectedIndex(selected);
            if (typeof setCurrentSpinnerQuestion === 'function') {
                setCurrentSpinnerQuestion(questions[selected]);
            }
        }, 3000);
    };

    useEffect(() => {
        if (started && remainingIndexes.length > 0 && !spinning && selectedIndex === null) {
            handleSpin();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [started, remainingIndexes, spinning, selectedIndex]);

    const handleAnswerClick = (index) => {
        if (answered) return;

        setClickedIndex(index);
        setAnswered(true);

        const selectedQuestion = questions[selectedIndex];
        const userAnswer = questions[index].correctAnswer;

        // Zavolej hlavní handleAnswer z Play.jsx
        if (handleAnswer) {
            handleAnswer(userAnswer, selectedQuestion);
        }

        setTimeout(() => {
            if (remainingIndexes.length > 0) {
                handleSpin();
            } else {
                if (onComplete) {
                    setTimeout(() => {
                        onComplete();
                    }, 1000);
                }
            }
        }, 2500);
    };

    return (
        <div className="spinner-container">
            <div className="spinner-pointer" />

            {!started && (
                <button
                    onClick={() => {
                        setStarted(true);
                        setRemainingIndexes([...Array(questions.length).keys()]);
                    }}
                    disabled={spinning}
                    className="spinButton btn btn-primary"
                >
                    {t('spin')}
                </button>
            )}


            <div className="spinner" ref={spinnerRef}>
                {questions.map((item, index) => {
                    const itemCount = questions.length;
                    const angle = (360 / itemCount) * index;
                    const isSelected = selectedIndex === index;
                    return (
                        <div
                            key={index}
                            className={`spinner-item ${isSelected ? 'selected' : ''}`}
                            style={{
                                transform: `rotate(${angle}deg) translate(0, ${isSelected ? '0' : '-150px'}) scale(${isSelected ? 4 : 1})`,
                                backgroundImage: `url(${item.image1})`,
                                opacity: selectedIndex !== null && !isSelected ? 0.4 : 1,
                                zIndex: isSelected ? 15 : 5,
                                backgroundSize: '90%',
                            }}
                        />
                    );
                })}
            </div>

            <div className="answer-buttons" style={{ opacity: selectedIndex !== null && !spinning ? '1' : '0' }}>
                {questions.map((item, index) => {
                    const isCorrect = index === selectedIndex;
                    const isClicked = index === clickedIndex;
                    return (
                        <button
                            key={index}
                            onClick={() => handleAnswerClick(index)}
                            disabled={answered && !(isCorrect || isClicked)}
                            className={
                                answered
                                    ? isClicked
                                        ? (isCorrect ? 'btn-green' : 'btn-red')
                                        : (isCorrect ? 'btn-green' : 'btn-secondary')
                                    : 'btn-secondary'
                            }
                        >
                            {item.correctAnswer}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default SpinnerPlay;
